import {
  linkifyRatingReferences,
  parseRatingReferenceId,
  RATING_REFERENCE_PAGES,
  ratingReferenceSearchTerm,
} from "~/pages/components/sidebars/impairmentRatingSidebar/ratingReferencePages";
import type { ChatbotLinkHandler } from "../types";

/**
 * Intercepts AMA Guides Table/Figure/section mentions in chatbot
 * responses (e.g. "Table 16-3", "Figure 17-5", "Section 17.2k") and routes
 * clicks to the AMA Guide PDF viewer via `ctx.onRequestAmaGuide`.
 *
 * The `linkify` pass rewrites bare mentions into markdown anchors
 * pointing at canonical ids (`#table-16-3`, `#ch-17-2k`); `matches` /
 * `onClick` then intercept those anchors at render time. Mapped
 * references jump straight to the known page; unmapped ones — every
 * section, and any table we have no page for — fall back to a BM25
 * search on the term the book itself uses.
 *
 * Mirrors the dispatch contract the IR sidebar uses in its
 * `onRequestAmaGuide` prop — both surfaces can share a single host
 * implementation when mounted alongside the viewer.
 */
export const amaGuideHandler: ChatbotLinkHandler = {
  id: "ama-guide",
  linkify: linkifyRatingReferences,
  matches: (href) => parseRatingReferenceId(href) !== null,
  onClick: (href, ctx) => {
    if (!ctx.onRequestAmaGuide) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[chatbotLinks] AMA Guide link clicked but no onRequestAmaGuide handler is wired:",
          href,
        );
      }
      return;
    }
    const id = parseRatingReferenceId(href);
    // `matches` already returned true, so `id` is non-null here — the guard
    // is retained only because the types can't encode that invariant.
    if (!id) return;
    // The book's own wording for this reference — "Table 16-3", but a bare
    // "17.2k" for a section, whose "Section" prefix is ours and appears
    // nowhere in the PDF text a search would match against.
    const term = ratingReferenceSearchTerm(id);
    if (!term) return;
    // Look up directly by id to skip a redundant parse inside
    // `lookupRatingReferencePage` (which would re-run `parseRatingReferenceId`).
    const page = RATING_REFERENCE_PAGES[id] ?? null;
    if (page !== null) {
      void ctx.onRequestAmaGuide({ page, highlight: term });
    } else {
      void ctx.onRequestAmaGuide({ query: term, highlight: term });
    }
  },
};
